import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../components/Layout/DashboardLayout';
import ChatMessage from '../components/ChatMessage';
import LoadingDots from '../components/LoadingDots';
import { chatAPI } from '../services/api';
import { Send, Trash2, MessageSquare, AlertCircle, Bot } from 'lucide-react';

const Chatbot = () => {
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(true);
    const [error, setError] = useState('');
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        fetchChatHistory();
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, loading]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const fetchChatHistory = async () => {
        try {
            const response = await chatAPI.getHistory();
            setMessages(response.data.messages || []);
        } catch (error) {
            console.error('Error fetching chat history:', error);
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();

        if (!inputMessage.trim() || loading) return;

        const userMessage = inputMessage.trim();
        setInputMessage('');
        setError('');
        setLoading(true);

        // Optimistically add user message
        const tempUserMessage = {
            role: 'user',
            content: userMessage,
            timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, tempUserMessage]);

        try {
            const response = await chatAPI.sendMessage(userMessage);

            // Replace temp message with actual and add assistant response
            setMessages(prev => {
                const withoutTemp = prev.slice(0, -1);
                return [
                    ...withoutTemp,
                    response.data.userMessage,
                    response.data.assistantMessage
                ];
            });
        } catch (error) {
            console.error('Error sending message:', error);
            setError('Failed to get response. Please try again.');
            // Remove the optimistic message on error
            setMessages(prev => prev.slice(0, -1));
        } finally {
            setLoading(false);
            inputRef.current?.focus();
        }
    };

    const handleClearHistory = async () => {
        if (!window.confirm('Are you sure you want to clear all chat history?')) return;

        try {
            await chatAPI.clearHistory();
            setMessages([]);
        } catch (error) {
            console.error('Error clearing history:', error);
            setError('Failed to clear history');
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage(e);
        }
    };

    return (
        <DashboardLayout>
            <div className="h-[calc(100vh-8rem)] flex flex-col">
                {/* Chat Header */}
                <div className="bg-white rounded-t-xl shadow-sm border border-gray-200 p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-gradient-to-br from-gray-700 to-gray-900 rounded-xl shadow-md">
                            <Bot className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h1 className="font-semibold text-gray-900">AI Study Assistant</h1>
                            <p className="text-sm text-gray-500">Ask any academic question</p>
                        </div>
                    </div>
                    <button
                        onClick={handleClearHistory}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Clear chat history"
                    >
                        <Trash2 className="w-5 h-5" />
                    </button>
                </div>

                {/* Chat Messages */}
                <div className="flex-1 bg-gray-50 border-x border-gray-200 overflow-y-auto p-4 space-y-4">
                    {loadingHistory ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center">
                                <div className="w-8 h-8 border-4 border-black border-t-transparent rounded-full animate-spin mx-auto"></div>
                                <p className="text-gray-500 mt-2">Loading chat history...</p>
                            </div>
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center">
                            <div className="p-4 bg-gray-200 rounded-full mb-4">
                                <MessageSquare className="w-10 h-10 text-gray-600" />
                            </div>
                            <h2 className="text-xl font-semibold text-gray-900 mb-2">Start a Conversation</h2>
                            <p className="text-gray-500 max-w-md">
                                Ask me anything about your studies! I can help with math, science, history, literature, and more.
                            </p>
                            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-lg">
                                {[
                                    'Explain the Pythagorean theorem',
                                    'What causes photosynthesis?',
                                    'Help me understand fractions',
                                    'What is Newton\'s first law?'
                                ].map((suggestion, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setInputMessage(suggestion)}
                                        className="p-3 text-left text-sm bg-white border border-gray-200 rounded-lg hover:border-black hover:bg-gray-50 transition-colors"
                                    >
                                        {suggestion}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <>
                            {messages.map((message, index) => (
                                <ChatMessage
                                    key={index}
                                    message={message}
                                    isUser={message.role === 'user'}
                                />
                            ))}
                            {loading && <LoadingDots />}
                        </>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* Error Message */}
                {error && (
                    <div className="bg-gray-100 border-x border-gray-200 px-4 py-2 flex items-center gap-2 text-gray-700">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        <span className="text-sm">{error}</span>
                    </div>
                )}

                {/* Chat Input */}
                <div className="bg-white rounded-b-xl shadow-sm border border-gray-200 p-4">
                    <form onSubmit={handleSendMessage} className="flex gap-3">
                        <div className="flex-1 relative">
                            <textarea
                                ref={inputRef}
                                value={inputMessage}
                                onChange={(e) => setInputMessage(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Type your question here..."
                                rows={1}
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-black focus:border-transparent outline-none resize-none"
                                style={{ minHeight: '48px', maxHeight: '120px' }}
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={!inputMessage.trim() || loading}
                            className="px-6 py-3 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-xl hover:from-gray-700 hover:to-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 shadow-md"
                        >
                            <Send className="w-5 h-5" />
                            <span className="hidden sm:inline">Send</span>
                        </button>
                    </form>
                    <p className="text-xs text-gray-400 mt-2 text-center">
                        Press Enter to send • Shift+Enter for new line
                    </p>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default Chatbot;
